/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { JSX } from 'preact';
import { createRef } from 'preact';
import { useCallback, useMemo, useRef, useState } from 'preact/hooks';
import { forwardRef, ForwardedRef } from 'preact/compat';
import { classNames } from '../utils/UNSAFE_classNames';
import { useId } from '../hooks/UNSAFE_useId';
import { useUser } from '../hooks/UNSAFE_useUser';
import { Column, ColumnWidths, TableViewportConfig } from '.';
import { TableColGroup } from '../PRIVATE_Table/TableColGroup';
import { TableDisplayBody } from './TableDisplayBody';
import { TableDisplayHeader } from './TableDisplayHeader';
import { TableDisplayFooter } from './TableDisplayFooter';
import { TableSkeletonRow } from '../PRIVATE_Table/TableSkeletonRow';
import { Props } from '.';
import { TableDisplayHandle } from './Table.types';
import { useDataManager } from '../PRIVATE_TableCommon/hooks/useDataManager';
import { useScrollHandling } from '../PRIVATE_Table/hooks/useScrollHandling';
import { useSizingManager } from '../PRIVATE_Table/hooks/useSizingManager';
import { innerTableStyles, scrollerStyles } from '../PRIVATE_Table/themes/Table.css';
import {
  tableSkeletonStyles,
  tableSkeletonStylesHC
} from '../PRIVATE_Table/themes/TableSkeletonStyles.css';
import { TableVariantOptions } from '../PRIVATE_Table/themes/TableStyles.css';
import { TableRedwoodTheme } from '../PRIVATE_Table/themes/redwood/TableTheme';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useTestId } from '../hooks/UNSAFE_useTestId';

export type TableDisplayColumn<K, D, C> = {
  key: C;
  value: Column<K, D, C>;
};

export type BorderEdges = {
  hasStart: boolean;
  hasEnd: boolean;
  width: number;
  offset: number;
}[];

export type AppliedColumnWidths<C extends string> = {
  netColumnWidths: ColumnWidths<C>;
  fullColumnWidths: number[];
  realColumnWidths: number[];
  borderEdges: BorderEdges;
  borderOffset: number;
  totalBorderOffset: number;
};

export type StickyColumnInfo = {
  stickyColumns: number[];
  firstStickyColumnIndex?: number;
  finalStickyColumnIndex?: number;
};

export type ColumnHeaderFooterInfo<C> = {
  key: C;
  isHeader: boolean;
};

export type SampledWidths<C extends string> = {
  minWidths: ColumnWidths<C>;
  weightWidths: ColumnWidths<C>;
};

export type ViewportInfo = {
  isExternalScroller: boolean;
  viewportConfig: TableViewportConfig;
};

export type SizingInfo =
  | {
      isInitialRender: true;
      boxWidth?: number;
      contentWidth?: number;
      boxHeight?: number;
      contentHeight?: number;
      isScrollbarSizeKnown: false;
      defaultScrollbarWidth?: number;
      defaultScrollbarHeight?: number;
    }
  | {
      isInitialRender: false;
      boxWidth: number;
      contentWidth: number;
      boxHeight: number;
      contentHeight: number;
      isScrollbarSizeKnown: true;
      defaultScrollbarWidth: number;
      defaultScrollbarHeight: number;
    }
  | {
      isInitialRender: false;
      boxWidth: number;
      contentWidth: number;
      boxHeight: number;
      contentHeight: number;
      isScrollbarSizeKnown: false;
      defaultScrollbarWidth?: number;
      defaultScrollbarHeight?: number;
    };

/**
 * Component that renders data in a flat table.
 */
export const TableDisplayBase = forwardRef(function TableDisplayBase<
  K extends string | number,
  D,
  C extends string
>({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  getAccessibleRowHeaders,
  columns,
  columnOrder,
  columnWidths,
  viewportConfig,
  layout = 'contents',
  data,
  getRowKey,
  gridlines,
  sortCriterion,
  onSortCriterionChange,
  horizontalScrollPositionOverride,
  verticalScrollPositionOverride,
  noDataRenderer,
  scrollerRef,
  testId
}: Props<K, D, C>) {
  const hasMore = false;
  const onLoadMore = () => {};

  const tableId = useId();
  const testIdProps = useTestId(testId);

  // rootRef is callback-based to meet useResizeObserver hook's state management requirements
  const [rootRef, setRootRef] = useState(createRef<HTMLDivElement>());
  const rootRefCallback = useCallback((node: HTMLDivElement | null) => {
    setRootRef({ current: node });
  }, []);
  const innerTableRef = useRef<HTMLTableElement>(null);

  const { direction, forcedColors } = useUser();
  const isHighContrast = forcedColors === 'active';
  const isRtl = direction === 'rtl';

  const { managedData: tableData, onManagedLoadMore: onTableLoadMore } = useDataManager<K, D>({
    data,
    getRowKey,
    hasMore,
    onLoadMore
  });

  const {
    columnBorderEdges,
    isLayoutRequired,
    viewportInfo,
    appliedColumnWidths,
    sizingInfoRef,
    overflowInfoRef,
    hasVerticalUnderflow,
    stickyEdgesState,
    setStickyEdgesState,
    stickyColumnInfo,
    scrollerRefCallback
  } = useSizingManager<K, D, C>({
    rootRef,
    innerTableRef,
    tableId,
    tableData,
    viewportConfig,
    externalScrollerAccessRef: scrollerRef,
    layout,
    columns,
    columnOrder,
    columnWidths,
    isRtl,
    isHighContrast
  });

  // memoize realized columns array on 'columns' and 'columnOrder'
  const columnsArray = useMemo(() => {
    const newColumnsArray = [];
    if (columnOrder != null) {
      for (const key of columnOrder) {
        if (columns[key] != null) {
          newColumnsArray.push({
            key: key,
            value: columns[key]
          });
        }
      }
    } else {
      for (const [key, value] of Object.entries(columns)) {
        newColumnsArray.push({
          key: key as C,
          value: value as Column<K, D, C>
        });
      }
    }
    return newColumnsArray;
  }, [columnOrder, columns]);

  const isPendingLayout = layout === 'pending';

  const scrollHandlingProps = useScrollHandling({
    rootRef,
    viewportInfo,
    tableId,
    isLayoutRequired,
    sizingInfoRef,
    overflowInfoRef,
    columnsArray,
    stickyColumnInfo,
    stickyEdgesState,
    setStickyEdgesState,
    fullColumnWidths: appliedColumnWidths?.fullColumnWidths,
    horizontalScrollPositionOverride,
    verticalScrollPositionOverride,
    isPendingLayout,
    isRtl
  });

  // aria-rowcount includes header and footer rows
  const hasFooters = columnsArray.some((column: TableDisplayColumn<K, D, C>) => {
    return column.value.footerRenderer != null || column.value.footerText != null;
  });
  const ariaRowCount = hasMore || isPendingLayout ? -1 : tableData.length + (hasFooters ? 2 : 1);
  const ariaColumnCount = Math.max(columnsArray.length, 1);

  // initialize base Table style classes
  const { classes: themeClasses } = useComponentTheme<TableVariantOptions>(TableRedwoodTheme, {
    pendingLayout: isPendingLayout ? 'isPendingLayout' : 'notPendingLayout',
    showFocusRing: 'notShowFocusRing',
    externalScroller: viewportInfo.isExternalScroller ? 'isExternalScroller' : 'notExternalScroller'
  });

  let scrollerStyle;
  let scrollerPadding = '';
  const scrollerClassArray = [scrollerStyles.base];
  if (viewportInfo.isExternalScroller) {
    scrollerClassArray.push(scrollerStyles.externalScroller);
  } else if (!sizingInfoRef.current.isScrollbarSizeKnown) {
    scrollerClassArray.push(scrollerStyles.forceScroll);
  } else {
    if (overflowInfoRef.current.isHorizontal) {
      scrollerClassArray.push(scrollerStyles.horizontalScroll);
    } else {
      scrollerClassArray.push(scrollerStyles.noHorizontalScroll);
    }
    if (overflowInfoRef.current.isVertical) {
      scrollerClassArray.push(scrollerStyles.verticalScroll);
    } else {
      scrollerClassArray.push(scrollerStyles.noVerticalScroll);
      scrollerPadding = `padding-inline-end: ${sizingInfoRef.current.defaultScrollbarWidth}px;`;
    }
  }
  // hide the table contents when layout is required and otherwise valid
  if (isLayoutRequired && columnsArray.length > 0) {
    scrollerStyle = `${scrollerPadding}opacity:0.001;`;
  } else {
    scrollerStyle = scrollerPadding;
  }

  const scrollerClasses = classNames(scrollerClassArray);

  const classes = classNames([themeClasses]);
  const tableClasses = classNames([innerTableStyles.base]);

  const startStickyEdge = stickyEdgesState.start;
  const endStickyEdge = stickyEdgesState.end;

  const hasVerticalGridlines = gridlines?.vertical === 'visible';
  const hasHorizontalGridlines = gridlines?.horizontal !== 'hidden';

  const getTableBodyProps = () => {
    return {
      rootRef: rootRef,
      viewportInfo: viewportInfo,
      tableId: tableId,
      isValidRender: columnsArray.length > 0,
      tableData: tableData,
      columnsArray: columnsArray,
      getAccessibleRowHeaders: getAccessibleRowHeaders,
      hasVerticalGridlines: hasVerticalGridlines,
      hasHorizontalGridlines: hasHorizontalGridlines,
      hasFooters: hasFooters,
      hasVerticalUnderflow: hasVerticalUnderflow,
      hasMore: hasMore,
      onLoadMore: onTableLoadMore,
      noDataRenderer: noDataRenderer,
      startStickyEdge: startStickyEdge,
      endStickyEdge: endStickyEdge,
      firstStickyColumnIndex: stickyColumnInfo.firstStickyColumnIndex,
      finalStickyColumnIndex: stickyColumnInfo.finalStickyColumnIndex,
      columnBorderEdges: columnBorderEdges,
      isRtl: isRtl,
      isHighContrast: isHighContrast
    };
  };

  const getTableColGroupProps = () => {
    let columnWidthsArray;
    if (!isLayoutRequired && appliedColumnWidths?.realColumnWidths != null) {
      columnWidthsArray = appliedColumnWidths.realColumnWidths;
    } /* else if (layout === 'fixed') {
      // default to 100px for each column when layout is not yet available
      columnWidthsArray = [];
      for (let i = 0; i < columnsArray.length; i++) {
        columnWidthsArray[i] = 100;
      }
    } */ else {
      columnWidthsArray = [];
      for (let i = 0; i < columnsArray.length; i++) {
        columnWidthsArray[i] = undefined;
      }
    }
    return {
      tableId: tableId,
      isRendered: columnsArray.length > 0,
      columnWidthsArray: columnWidthsArray
    };
  };

  const getTableHeaderProps = () => {
    return {
      tableId: tableId,
      isRendered: columnsArray.length > 0,
      columnsArray: columnsArray,
      hasVerticalGridlines: hasVerticalGridlines,
      sortCriterion: sortCriterion,
      onSortCriterionChange: onSortCriterionChange,
      stickyTopOffset: viewportInfo.viewportConfig.scrollerOffsetTop,
      startStickyEdge: startStickyEdge,
      endStickyEdge: endStickyEdge,
      firstStickyColumnIndex: stickyColumnInfo.firstStickyColumnIndex,
      finalStickyColumnIndex: stickyColumnInfo.finalStickyColumnIndex,
      columnBorderEdges: columnBorderEdges,
      isRtl: isRtl,
      isHighContrast: isHighContrast
    };
  };

  const getTableFooterProps = () => {
    return {
      tableId: tableId,
      isRendered: hasFooters,
      columnsArray: columnsArray,
      hasVerticalGridlines: hasVerticalGridlines,
      stickyBottomOffset: viewportInfo.viewportConfig.scrollerOffsetBottom,
      startStickyEdge: startStickyEdge,
      endStickyEdge: endStickyEdge,
      firstStickyColumnIndex: stickyColumnInfo.firstStickyColumnIndex,
      finalStickyColumnIndex: stickyColumnInfo.finalStickyColumnIndex,
      columnBorderEdges: columnBorderEdges,
      dataRowCount: tableData.length,
      isRtl: isRtl,
      isHighContrast: isHighContrast
    };
  };

  const getTableStyle = () => {
    if (!isLayoutRequired && appliedColumnWidths?.realColumnWidths != null) {
      let totalWidth = 0;
      for (let i = 0; i < appliedColumnWidths.realColumnWidths.length; i++) {
        totalWidth += appliedColumnWidths.realColumnWidths[i];
      }
      return `width:${totalWidth}px;table-layout:fixed;`;
    } else if (columnsArray.length > 0) {
      /* if (layout === 'fixed') {
        // default to 100px for each column when layout is not yet available
        return `width:${columnsArray.length * 100}px;table-layout:fixed;`;
      } else { */
      // shrink table down as much as possible when a 'contents' layout is required, otherwise
      // the sampled column widths will not reflect the true weight sizes with line-clamping
      return 'width:max-content;will-change:width;';
      // }
    }
    // no columns are present
    return 'width:100%;';
  };

  const skeletonStyles = isHighContrast ? tableSkeletonStylesHC : tableSkeletonStyles;
  const getPendingLayoutSkeletons = () => {
    return isPendingLayout ? (
      <div class={skeletonStyles.pendingLayout}>
        <table role={'presentation'} style={'width:100%;table-layout:fixed;'}>
          <tbody role={'presentation'}>
            <TableSkeletonRow
              tableId={tableId}
              hasTabIndex={false}
              colspan={ariaColumnCount}
              isPendingLayout={true}
              isShowFocusRing={false}
              isEmbedded={false}
              isHighContrast={isHighContrast}
            />
          </tbody>
        </table>
      </div>
    ) : undefined;
  };

  return (
    <>
      <div {...testIdProps} ref={rootRefCallback} class={classes} id={tableId}>
        <div
          ref={scrollerRefCallback}
          aria-colcount={ariaColumnCount}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-rowcount={ariaRowCount}
          class={scrollerClasses}
          style={scrollerStyle}
          role={'table'}
          tabIndex={0}
          {...scrollHandlingProps}>
          <table
            ref={innerTableRef}
            aria-hidden={isPendingLayout ? true : undefined}
            class={tableClasses}
            role={'presentation'}
            style={getTableStyle()}
            data-oj-table-inner-table={tableId}>
            <TableColGroup {...getTableColGroupProps()} />
            <TableDisplayHeader {...getTableHeaderProps()} />
            <TableDisplayBody {...getTableBodyProps()} />
            <TableDisplayFooter {...getTableFooterProps()} />
          </table>
        </div>
        {getPendingLayoutSkeletons()}
      </div>
    </>
  );
}) as <K extends string | number, D, C extends string>(
  p: Props<K, D, C> & { ref?: ForwardedRef<TableDisplayHandle<K, C>> }
) => JSX.Element;
